<template>
  <div>
    <vx-card>
      <div class="flex justify-between items-center mb-4">
        <h2>HaioFlash Job Manager</h2>
        <div class="flex items-center gap-3">
          <vs-button color="primary" type="border" size="small" @click="goToMonitor()">
            <feather-icon icon="ActivityIcon" svgClasses="w-4 h-4 mr-1"></feather-icon>
            Live Monitor
          </vs-button>
          <vs-button color="success" size="small" @click="loadAll()">
            <feather-icon icon="RefreshCwIcon" svgClasses="w-4 h-4 mr-1"></feather-icon>
            Refresh
          </vs-button>
        </div>
      </div>

      <!-- Stats Cards -->
      <div v-if="stats" class="stats-grid mb-6">
        <div class="stat-card" style="cursor:pointer" @click="setType('pending')">
          <div class="stat-icon" style="background:#7367F0;">
            <feather-icon icon="ClockIcon" svgClasses="text-white"></feather-icon>
          </div>
          <div class="stat-content">
            <p class="stat-label">Pending</p>
            <h3 class="stat-value">{{ totalPending }}</h3>
          </div>
        </div>
        <div class="stat-card" style="cursor:pointer" @click="setType('processing')">
          <div class="stat-icon" style="background:#00CFE8;">
            <feather-icon icon="PlayIcon" svgClasses="text-white"></feather-icon>
          </div>
          <div class="stat-content">
            <p class="stat-label">Processing</p>
            <h3 class="stat-value">{{ totalProcessing }}</h3>
          </div>
        </div>
        <div class="stat-card" style="cursor:pointer" @click="setType('failed')">
          <div class="stat-icon" style="background:#EA5455;">
            <feather-icon icon="XCircleIcon" svgClasses="text-white"></feather-icon>
          </div>
          <div class="stat-content">
            <p class="stat-label">Failed</p>
            <h3 class="stat-value">{{ stats.failed }}</h3>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon" style="background:#FF9F43;">
            <feather-icon icon="AlertTriangleIcon" svgClasses="text-white"></feather-icon>
          </div>
          <div class="stat-content">
            <p class="stat-label">Stuck (&gt;10min)</p>
            <h3 class="stat-value">{{ stats.stuck }}</h3>
          </div>
        </div>
      </div>

      <!-- Queue backlog warning -->
      <vs-alert v-if="stats && stats.queues && stats.queues.default > 1000" color="warning" class="mb-4">
        <feather-icon icon="AlertTriangleIcon" class="mr-2"></feather-icon>
        <strong>Default queue backlog:</strong> {{ stats.queues.default }} jobs — lifecycle jobs may be delayed. Use "Move to haioflash-default" on stuck jobs.
      </vs-alert>

      <!-- Tabs -->
      <vs-tabs v-model="activeTab" class="mb-4">
        <vs-tab label="⏳ Pending" @click="setType('pending')"></vs-tab>
        <vs-tab label="▶ Processing" @click="setType('processing')"></vs-tab>
        <vs-tab label="❌ Failed" @click="setType('failed')"></vs-tab>
      </vs-tabs>

      <!-- Filters -->
      <div class="flex flex-wrap gap-3 mb-4">
        <vs-select v-model="filters.queue" placeholder="All Queues" @change="loadJobs()">
          <vs-select-item value="" text="All Queues"></vs-select-item>
          <vs-select-item value="haioflash-default" text="haioflash-default"></vs-select-item>
          <vs-select-item value="default" text="default"></vs-select-item>
        </vs-select>

        <vs-select v-model="filters.job_name" placeholder="All Job Types" @change="loadJobs()">
          <vs-select-item value="" text="All Job Types"></vs-select-item>
          <vs-select-item value="CreateVmOnMicroservice" text="Create VM"></vs-select-item>
          <vs-select-item value="StartVmOnMicroservice" text="Start VM"></vs-select-item>
          <vs-select-item value="StopVmOnMicroservice" text="Stop VM"></vs-select-item>
          <vs-select-item value="ReinstallVmOnMicroservice" text="Reinstall VM"></vs-select-item>
          <vs-select-item value="DeleteVmOnMicroservice" text="Delete VM"></vs-select-item>
          <vs-select-item value="SyncVmStatus" text="Sync Status"></vs-select-item>
          <vs-select-item value="ConfigureIpv4Addon" text="Configure IPv4 Addon"></vs-select-item>
          <vs-select-item value="ProcessHourlyBilling" text="Hourly Billing"></vs-select-item>
          <vs-select-item value="TrackVmTraffic" text="Track Traffic"></vs-select-item>
          <vs-select-item value="CollectVmTrafficVolume" text="Collect Traffic Volume"></vs-select-item>
          <vs-select-item value="CollectVmStats" text="Collect Stats"></vs-select-item>
        </vs-select>

        <vs-input
          v-model="filters.vm_id"
          placeholder="VM ID"
          type="number"
          style="width:120px"
          @keyup.enter="loadJobs()"
        ></vs-input>

        <vs-button color="primary" size="small" @click="loadJobs()">
          <feather-icon icon="SearchIcon" svgClasses="w-4 h-4 mr-1"></feather-icon>
          Search
        </vs-button>
        <vs-button color="dark" type="border" size="small" @click="clearFilters()">Clear</vs-button>
      </div>

      <!-- Table -->
      <vs-table
        :data="jobs"
        :no-data-text="loading ? 'Loading...' : 'No jobs found'"
        stripe
        class="job-table"
      >
        <template slot="thead">
          <vs-th>ID</vs-th>
          <vs-th>Job</vs-th>
          <vs-th>Queue</vs-th>
          <vs-th>VM</vs-th>
          <vs-th>User</vs-th>
          <vs-th>Status</vs-th>
          <vs-th>Age</vs-th>
          <vs-th>Actions</vs-th>
        </template>

        <template slot-scope="{ data }">
          <vs-tr v-for="job in data" :key="job.id" :class="rowClass(job)">
            <vs-td>
              <span class="text-xs font-mono text-grey">#{{ job.id }}</span>
            </vs-td>

            <vs-td>
              <span class="job-badge" :class="jobBadgeClass(job.job_name)">
                {{ job.job_label || job.job_name }}
              </span>
            </vs-td>

            <vs-td>
              <span class="queue-badge" :class="job.queue === 'haioflash-default' ? 'queue-hf' : 'queue-default'">
                {{ job.queue }}
              </span>
            </vs-td>

            <vs-td>
              <div v-if="job.vm">
                <p class="text-sm font-semibold">{{ job.vm.name }}</p>
                <p class="text-xs text-grey" dir="ltr">{{ job.vm.microvm_id }}</p>
                <span class="vm-status-badge" :class="'vm-' + job.vm.status">{{ job.vm.status }}</span>
              </div>
              <span v-else class="text-grey text-xs">—</span>
            </vs-td>

            <vs-td>
              <div v-if="job.user">
                <p class="text-sm">{{ job.user.name || '—' }}</p>
                <p class="text-xs text-grey">#{{ job.user.id }}</p>
                <p class="text-xs text-grey">{{ job.user.email }}</p>
              </div>
              <span v-else class="text-grey text-xs">—</span>
            </vs-td>

            <vs-td>
              <div v-if="currentType === 'failed'">
                <span class="status-badge status-failed">Failed</span>
                <p class="text-xs text-grey mt-1" v-if="job.failed_at">{{ formatDate(job.failed_at) }}</p>
              </div>
              <div v-else-if="job.reserved_at">
                <span class="status-badge status-processing">Processing</span>
                <p class="text-xs text-grey mt-1">attempt #{{ job.attempts + 1 }}</p>
              </div>
              <div v-else>
                <span class="status-badge status-pending">Pending</span>
                <p class="text-xs text-grey mt-1">attempt {{ job.attempts }}</p>
              </div>
            </vs-td>

            <vs-td>
              <span class="text-xs text-grey" dir="ltr">{{ ageLabel(job) }}</span>
            </vs-td>

            <vs-td>
              <div class="flex gap-2 flex-wrap">
                <!-- Retry failed -->
                <vs-button
                  v-if="currentType === 'failed'"
                  color="primary"
                  type="border"
                  size="small"
                  @click="retryJob(job)"
                  :disabled="actionLoading[job.id]"
                >
                  <feather-icon icon="RotateCwIcon" svgClasses="w-3 h-3 mr-1"></feather-icon>
                  Retry
                </vs-button>

                <!-- Move to haioflash-default if stuck in default queue -->
                <vs-button
                  v-if="currentType !== 'failed' && job.queue === 'default' && isLifecycleJob(job.job_name)"
                  color="warning"
                  type="border"
                  size="small"
                  @click="moveToHaioflashQueue(job)"
                  :disabled="actionLoading[job.id]"
                  title="Move to haioflash-default for faster processing"
                >
                  <feather-icon icon="ArrowRightIcon" svgClasses="w-3 h-3 mr-1"></feather-icon>
                  Move Queue
                </vs-button>

                <!-- Delete failed -->
                <vs-button
                  v-if="currentType === 'failed'"
                  color="danger"
                  type="border"
                  size="small"
                  @click="confirmDelete(job)"
                  :disabled="actionLoading[job.id]"
                >
                  <feather-icon icon="TrashIcon" svgClasses="w-3 h-3"></feather-icon>
                </vs-button>

                <!-- Show error -->
                <vs-button
                  v-if="currentType === 'failed' && job.exception"
                  color="dark"
                  type="flat"
                  size="small"
                  @click="showException(job)"
                >
                  <feather-icon icon="InfoIcon" svgClasses="w-3 h-3"></feather-icon>
                </vs-button>
              </div>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>

      <!-- Pagination -->
      <div class="flex justify-between items-center mt-4">
        <p class="text-sm text-grey">
          Total: <strong>{{ meta.total }}</strong> |
          Page {{ meta.page }} of {{ totalPages }}
        </p>
        <div class="flex gap-2">
          <vs-button
            size="small"
            type="border"
            :disabled="meta.page <= 1"
            @click="changePage(meta.page - 1)"
          >&laquo; Prev</vs-button>
          <vs-button
            size="small"
            type="border"
            :disabled="meta.page >= totalPages"
            @click="changePage(meta.page + 1)"
          >Next &raquo;</vs-button>
        </div>
      </div>
    </vx-card>

    <!-- Exception Modal -->
    <vs-popup :active.sync="showExceptionModal" title="Job Error" style="z-index:99999">
      <div v-if="selectedJob">
        <p class="font-semibold mb-2">{{ selectedJob.job_name }} — Job #{{ selectedJob.id }}</p>
        <pre class="exception-pre">{{ selectedJob.exception }}</pre>
      </div>
    </vs-popup>
  </div>
</template>

<script>
export default {
  name: 'HaioFlashJobsManager',
  data() {
    return {
      activeTab: 0,
      currentType: 'pending',
      jobs: [],
      stats: null,
      loading: false,
      filters: { queue: '', job_name: '', vm_id: '' },
      meta: { total: 0, page: 1, per_page: 25 },
      actionLoading: {},
      showExceptionModal: false,
      selectedJob: null,
      lifecycleJobs: [
        'CreateVmOnMicroservice', 'StartVmOnMicroservice', 'StopVmOnMicroservice',
        'ReinstallVmOnMicroservice', 'DeleteVmOnMicroservice', 'SyncVmStatus', 'ConfigureIpv4Addon',
      ],
    }
  },
  computed: {
    totalPending() {
      if (!this.stats || !this.stats.pending) return 0
      return this.stats.pending.reduce(function (sum, q) { return sum + q.count }, 0)
    },
    totalProcessing() {
      if (!this.stats || !this.stats.processing) return 0
      return this.stats.processing.reduce(function (sum, q) { return sum + q.count }, 0)
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.meta.total / this.meta.per_page))
    },
  },
  methods: {
    async loadStats() {
      try {
        const { data } = await this.$http.get('/admin/haioflash/jobs/stats')
        if (data.success) this.stats = data.data
      } catch (e) {
        console.error('Failed to load stats', e)
      }
    },
    async loadJobs() {
      this.loading = true
      try {
        const params = {
          type: this.currentType,
          queue: this.filters.queue,
          job_name: this.filters.job_name,
          vm_id: this.filters.vm_id,
          page: this.meta.page,
          per_page: this.meta.per_page,
        }
        const { data } = await this.$http.get('/admin/haioflash/jobs', { params })
        if (data.success) {
          this.jobs = data.data
          this.meta = data.meta || this.meta
        }
      } catch (e) {
        this.$vs.notify({ title: 'Error', text: 'Failed to load jobs', color: 'danger' })
      } finally {
        this.loading = false
      }
    },
    loadAll() {
      this.loadStats()
      this.loadJobs()
    },
    setType(type) {
      this.currentType = type
      this.meta.page = 1
      this.loadJobs()
    },
    clearFilters() {
      this.filters = { queue: '', job_name: '', vm_id: '' }
      this.meta.page = 1
      this.loadJobs()
    },
    changePage(page) {
      this.meta.page = page
      this.loadJobs()
    },
    async retryJob(job) {
      this.$set(this.actionLoading, job.id, true)
      try {
        const { data } = await this.$http.post('/admin/haioflash/jobs/failed/' + job.id + '/retry')
        if (data.success) {
          this.$vs.notify({ title: 'Retried', text: data.message, color: 'success' })
          this.loadAll()
        }
      } catch (e) {
        this.$vs.notify({ title: 'Error', text: 'Retry failed', color: 'danger' })
      } finally {
        this.$set(this.actionLoading, job.id, false)
      }
    },
    async moveToHaioflashQueue(job) {
      this.$set(this.actionLoading, job.id, true)
      try {
        const { data } = await this.$http.post('/admin/haioflash/jobs/' + job.id + '/move-to-haioflash-queue')
        if (data.success) {
          this.$vs.notify({ title: 'Moved', text: data.message, color: 'success' })
          this.loadAll()
        }
      } catch (e) {
        this.$vs.notify({ title: 'Error', text: 'Move failed', color: 'danger' })
      } finally {
        this.$set(this.actionLoading, job.id, false)
      }
    },
    confirmDelete(job) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Delete Failed Job',
        text: 'Are you sure you want to delete this failed job? This cannot be undone.',
        accept: function () { this.deleteJob(job) }.bind(this),
      })
    },
    async deleteJob(job) {
      this.$set(this.actionLoading, job.id, true)
      try {
        const { data } = await this.$http.delete('/admin/haioflash/jobs/failed/' + job.id)
        if (data.success) {
          this.$vs.notify({ title: 'Deleted', text: data.message, color: 'success' })
          this.loadAll()
        }
      } catch (e) {
        this.$vs.notify({ title: 'Error', text: 'Delete failed', color: 'danger' })
      } finally {
        this.$set(this.actionLoading, job.id, false)
      }
    },
    showException(job) {
      this.selectedJob = job
      this.showExceptionModal = true
    },
    goToMonitor() {
      this.$router.push('/haioflash/jobs/monitor')
    },
    isLifecycleJob(name) {
      return this.lifecycleJobs.indexOf(name) !== -1
    },
    rowClass(job) {
      if (this.currentType === 'failed') return 'row-failed'
      if (job.reserved_at) return 'row-processing'
      return ''
    },
    jobBadgeClass(name) {
      var destructive = ['DeleteVmOnMicroservice', 'ReinstallVmOnMicroservice']
      var creation = ['CreateVmOnMicroservice']
      var control = ['StartVmOnMicroservice', 'StopVmOnMicroservice']
      if (destructive.indexOf(name) !== -1) return 'job-danger'
      if (creation.indexOf(name) !== -1) return 'job-success'
      if (control.indexOf(name) !== -1) return 'job-info'
      return 'job-default'
    },
    ageLabel(job) {
      var ts = job.created_at || job.failed_at
      if (!ts) return '—'
      var now = new Date()
      var then = new Date(ts)
      var diff = Math.floor((now - then) / 1000)
      if (diff < 60) return diff + 's ago'
      if (diff < 3600) return Math.floor(diff / 60) + 'm ago'
      if (diff < 86400) return Math.floor(diff / 3600) + 'h ago'
      return Math.floor(diff / 86400) + 'd ago'
    },
    formatDate(d) {
      if (!d) return '—'
      return new Date(d).toLocaleString('fa-IR')
    },
  },
  created() {
    this.loadAll()
  },
}
</script>

<style scoped>
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}
@media (max-width: 768px) {
  .stats-grid { grid-template-columns: repeat(2, 1fr); }
}
.stat-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  border: 1px solid #f0f0f0;
  transition: box-shadow 0.2s;
}
.stat-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.12); }
.stat-icon {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.stat-label { font-size: 12px; color: #888; margin: 0; }
.stat-value { font-size: 22px; font-weight: 700; margin: 2px 0 0 0; }
.job-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
.job-success { background: #e8f7ee; color: #28C76F; }
.job-danger  { background: #fde8e8; color: #EA5455; }
.job-info    { background: #e0f7fa; color: #00CFE8; }
.job-default { background: #f0f0f0; color: #555; }
.queue-badge {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
}
.queue-hf      { background: #ede8ff; color: #7367F0; }
.queue-default { background: #fff0e0; color: #FF9F43; }
.status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}
.status-pending    { background: #f0f0f0; color: #888; }
.status-processing { background: #e0f7fa; color: #00CFE8; }
.status-failed     { background: #fde8e8; color: #EA5455; }
.vm-status-badge {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 600;
  margin-top: 2px;
}
.vm-running  { background: #e8f7ee; color: #28C76F; }
.vm-stopped  { background: #f0f0f0; color: #888; }
.vm-creating { background: #fff0e0; color: #FF9F43; }
.vm-suspended { background: #fde8e8; color: #EA5455; }
.vm-error    { background: #fde8e8; color: #EA5455; }
.row-failed   { background: #fff8f8 !important; }
.row-processing { background: #f0fbff !important; }
.exception-pre {
  background: #1a1a2e;
  color: #e0e0e0;
  padding: 16px;
  border-radius: 8px;
  font-size: 11px;
  line-height: 1.5;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 400px;
  overflow-y: auto;
}
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }
</style>
